import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { EventDetail, EventListItem } from '../../api/types';
import { AgentEventTimeline } from './AgentEventTimeline';

// 권위: docs/ARCH_DASHBOARD.md §4.4 상세 뷰 —
//   · 기본 필드 + payload JSON
//   · 관련 엔티티 딥링크 (symbol → 화면 6, order_id → 화면 2, run_id → 화면 4 자기 자신)
//   · "연관 이벤트 체인" 탭 (trace_id 기준)
//   · 읽음 처리 버튼

type Tab = 'detail' | 'chain';

interface Props {
  detail: EventDetail | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onClose: () => void;
  onAck: (note: string) => void;
  ackBusy: boolean;

  // 연관 이벤트 체인 (trace_id) — 컨테이너가 주입.
  chain: EventListItem[];
  chainLoading: boolean;
}

export function AgentHistoryDetail({
  detail,
  isLoading,
  isError,
  error,
  onClose,
  onAck,
  ackBusy,
  chain,
  chainLoading,
}: Props): JSX.Element {
  const [note, setNote] = useState('');
  const [tab, setTab] = useState<Tab>('detail');
  const acked = (detail?.acks?.length ?? 0) > 0;
  const navigate = useNavigate();

  const entityLinks = useMemo(() => extractEntityLinks(detail), [detail]);

  return (
    <div
      className="syslog-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="syslog-modal"
        role="dialog"
        aria-label="에이전트 이벤트 상세"
      >
        <header className="syslog-modal__header">
          {detail ? (
            <div className="syslog-modal__title-group">
              <span className={`syslog-badge syslog-badge--${detail.severity.toLowerCase()}`}>
                {detail.severity}
              </span>
              <code className="syslog-modal__code">{detail.eventCode}</code>
              <span className="syslog-modal__meta">
                {formatTs(detail.occurredAt)} · {detail.sourceAgent}
              </span>
            </div>
          ) : (
            <div className="syslog-modal__title-group">
              <span>상세</span>
            </div>
          )}
          <button
            type="button"
            className="syslog-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <nav className="agent-hist-tabs" aria-label="상세 탭">
          <button
            type="button"
            className={`agent-hist-tabs__tab${tab === 'detail' ? ' agent-hist-tabs__tab--active' : ''}`}
            onClick={() => setTab('detail')}
          >
            상세
          </button>
          <button
            type="button"
            className={`agent-hist-tabs__tab${tab === 'chain' ? ' agent-hist-tabs__tab--active' : ''}`}
            onClick={() => setTab('chain')}
          >
            연관 이벤트 체인 ({chain.length})
          </button>
        </nav>

        <div className="syslog-modal__body">
          {isLoading && <div className="syslog-empty">불러오는 중…</div>}
          {isError && (
            <div className="syslog-warning-banner">
              상세 조회 실패: {error?.message ?? 'unknown'}
            </div>
          )}

          {detail && tab === 'detail' && (
            <>
              <section className="syslog-section">
                <h4 className="syslog-section__title">요약</h4>
                <div>{detail.summary || <span className="syslog-muted">(없음)</span>}</div>
              </section>

              {entityLinks.length > 0 && (
                <section className="syslog-section">
                  <h4 className="syslog-section__title">관련 엔티티</h4>
                  <div className="agent-hist-entity-links">
                    {entityLinks.map((l) => (
                      <button
                        key={`${l.kind}-${l.value}`}
                        type="button"
                        className="syslog-btn syslog-btn--ghost"
                        onClick={() => navigate(l.to)}
                        title={l.hint}
                      >
                        <span className="syslog-muted">{l.kind}</span> · {l.value}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section className="syslog-section">
                <h4 className="syslog-section__title">Payload</h4>
                {detail.payload ? (
                  <JsonBlock value={detail.payload} />
                ) : (
                  <div className="syslog-muted">(payload 없음)</div>
                )}
                {detail.payloadRef && (
                  <div className="syslog-muted">payload_ref: {detail.payloadRef}</div>
                )}
              </section>

              {detail.entityRefs && Object.keys(detail.entityRefs).length > 0 && (
                <section className="syslog-section">
                  <h4 className="syslog-section__title">Entity refs</h4>
                  <JsonBlock value={detail.entityRefs} />
                </section>
              )}

              <section className="syslog-section">
                <h4 className="syslog-section__title">Meta</h4>
                <dl className="syslog-meta">
                  <dt>trace_id</dt>
                  <dd>{detail.traceId ?? <span className="syslog-muted">-</span>}</dd>
                  <dt>tags</dt>
                  <dd>
                    {detail.tags.length > 0 ? (
                      detail.tags.map((t) => (
                        <span key={t} className="syslog-tag">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="syslog-muted">-</span>
                    )}
                  </dd>
                  <dt>schema_version</dt>
                  <dd>{detail.schemaVersion ?? <span className="syslog-muted">-</span>}</dd>
                </dl>
              </section>

              <section className="syslog-section">
                <h4 className="syslog-section__title">읽음 처리 이력 ({detail.acks.length})</h4>
                {detail.acks.length === 0 ? (
                  <div className="syslog-muted">아직 읽음 처리되지 않았습니다.</div>
                ) : (
                  <ul className="syslog-acks">
                    {detail.acks.map((a, i) => (
                      <li key={`${a.ackBy}-${i}`}>
                        <b>{a.ackBy}</b> · {formatTs(a.ackAt)}
                        {a.note ? <span className="syslog-muted"> — {a.note}</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}

          {detail && tab === 'chain' && (
            <section className="syslog-section">
              <h4 className="syslog-section__title">trace_id 기반 연관 이벤트</h4>
              <AgentEventTimeline
                traceId={detail.traceId}
                items={chain}
                isLoading={chainLoading}
                currentEventId={detail.eventId}
              />
            </section>
          )}
        </div>

        <footer className="syslog-modal__actions">
          <input
            className="syslog-input syslog-input--note"
            type="text"
            placeholder="(선택) 읽음 처리 메모"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={acked || ackBusy || !detail}
          />
          <button
            type="button"
            className="syslog-btn syslog-btn--primary"
            disabled={acked || ackBusy || !detail}
            onClick={() => {
              onAck(note.trim() || '');
              setNote('');
            }}
          >
            {acked ? '이미 읽음 처리됨' : ackBusy ? '처리 중…' : '읽음 처리'}
          </button>
          <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onClose}>
            닫기
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── 내부 헬퍼 ────────────────────────────────────────

interface EntityLink {
  kind: string;
  value: string;
  to: string;
  hint: string;
}

function extractEntityLinks(detail: EventDetail | null): EntityLink[] {
  if (!detail) return [];
  const refs = (detail.entityRefs ?? {}) as Record<string, unknown>;
  const out: EntityLink[] = [];

  const symbol = (refs.symbol as string | undefined) ?? (refs.stockCode as string | undefined);
  if (symbol) {
    out.push({
      kind: 'symbol',
      value: symbol,
      // 화면 6 (Data Explorer) — 현재 구현 수준에서는 라우트만 연결.
      to: `/data?symbol=${encodeURIComponent(symbol)}`,
      hint: '데이터 탐색에서 열기',
    });
  }

  const orderId =
    (refs.order_id as string | undefined) ?? (refs.orderId as string | undefined);
  if (orderId) {
    out.push({
      kind: 'order_id',
      value: orderId,
      // 화면 2 (Live Positions) — 라우트만 연결.
      to: `/positions?order_id=${encodeURIComponent(orderId)}`,
      hint: '포지션 화면에서 열기',
    });
  }

  const runId = (refs.run_id as string | undefined) ?? (refs.runId as string | undefined);
  if (runId) {
    out.push({
      kind: 'run_id',
      value: runId,
      // 화면 4 자기 자신 — run_id 로 재필터.
      to: `/history?run_id=${encodeURIComponent(runId)}`,
      hint: '같은 run_id 이벤트 묶어 보기',
    });
  }

  return out;
}

function JsonBlock({ value }: { value: unknown }): JSX.Element {
  let json: string;
  try {
    json = JSON.stringify(value, null, 2);
  } catch {
    json = String(value);
  }
  return <pre className="syslog-json">{highlightJson(json)}</pre>;
}

function highlightJson(json: string): JSX.Element[] {
  const tokenRe =
    /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
  const out: JSX.Element[] = [];
  let lastIndex = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(json)) !== null) {
    if (m.index > lastIndex) {
      out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex, m.index)}</span>);
    }
    if (m[1] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__key">
          {m[1]}
        </span>,
        <span key={`t-${idx++}`}>:</span>
      );
    } else if (m[2] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__str">
          {m[2]}
        </span>
      );
    } else if (m[3] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__bool">
          {m[3]}
        </span>
      );
    } else if (m[4] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__num">
          {m[4]}
        </span>
      );
    }
    lastIndex = tokenRe.lastIndex;
  }
  if (lastIndex < json.length) {
    out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex)}</span>);
  }
  return out;
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
